import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

// common
import useService from 'common/hook/useService';

// screen
import type { ReplaceGeneralDetails } from 'modules/suppliers/types';

export type GeneralDetails = ReplaceGeneralDetails;

interface UseSupplierGeneralDetailsProps {
  customerOperationCode?: string;
  supplierProfileOperationCode?: string;
}

const useSupplierGeneralDetails = (props: UseSupplierGeneralDetailsProps) => {
  const { customerOperationCode, supplierProfileOperationCode } = props;
  const service = useService();

  return useQuery({
    queryKey: ['supplierGeneralDetails', customerOperationCode, supplierProfileOperationCode],
    queryFn: async () => {
      try {
        if (!service || !customerOperationCode || !supplierProfileOperationCode) {
          return null;
        }

        const res: GeneralDetails =
          await service.SupplierProfileController_getSupplierProfileDetail({
            params: {
              customerOperationCode,
              supplierProfileOperationCode,
            },
          });
        return res;
      } catch (error) {
        const axiosError = error as AxiosError<{ message?: string }>;
        const responseData = axiosError.response?.data;
        const errorMessage = responseData?.message || 'An error occurred';

        enqueueSnackbar({
          message: errorMessage,
          variant: 'error',
        });

        throw error;
      }
    },
    // Only enable the query when all required parameters are available
    enabled: !!(service && customerOperationCode && supplierProfileOperationCode),
    retry: 1,
  });
};

export default useSupplierGeneralDetails;
